import {
  DefaultNodeOptions,
  JsonError,
  NumberNode,
  widget,
  WidgetPlugin,
} from '@sagold/react-json-editor';

import { FormField } from '../../../components/formfield/FormField';
import { NumberInput } from '../../../components/numberinput/NumberInput';
import { ReadOnlyInput } from '../../../components/readonly-input/ReadOnlyInput';
import {
  FormatterOptions,
  numberFormatter,
} from '../../../components/table/valueFormatters';
import {
  isNullableInteger,
  isNullableNumber,
  useErrorTransformation,
} from '../widgetUtils';

export type NumberOptions = {
  icon?: string;
  tag?: string;
  swapIconPosition?: boolean;
  format?: Intl.NumberFormatOptions;
  /* add increment/decrement buttons */
  withButtons?: boolean;
  tableLayout?: boolean;
  showTitle?: boolean;
  showErrorWithColor?: boolean;
} & DefaultNodeOptions;

function errorsToString(errors: JsonError[]) {
  if (errors.length === 0) {
    return;
  }
  return errors.map((e) => e.message).join('\n');
}

function toNumber<N>(value: unknown, nan: N): number | N {
  const number = Number.parseFloat(String(value));
  return Number.isNaN(number) ? nan : number;
}

export const NumberWidget = widget<NumberNode<NumberOptions>, number | null>(
  ({ node, options, setValue }) => {
    const hasError = node.errors.length > 0;
    const isValidConst = node.schema.const != null && !hasError;

    const errors = useErrorTransformation(node.errors);

    const readOnlyNumber = numberFormatter(
      node.value,
      options as FormatterOptions
    );
    return (
      <FormField
        widgetType="number"
        node={node}
        errors={errors}
        options={options}
        tableLayout={options.tableLayout}
      >
        {options.readOnly ? (
          <ReadOnlyInput
            label={options.title}
            value={readOnlyNumber?.toString()}
            tableLayout={options.tableLayout}
            hasError={options.showErrorWithColor && node.errors.length > 0}
          />
        ) : (
          <NumberInput
            allowDecimal={isNullableNumber(node.schema.type)}
            clampBehaviour="strict"
            disabled={options.disabled || isValidConst}
            error={errorsToString(node.errors)}
            id={node.id}
            min={node.schema.minimum}
            max={node.schema.maximum}
            onChange={(value) => setValue(toNumber(value, null))}
            placeholder={options.placeholder}
            readOnly={options.readOnly}
            required={options.required}
            tableLayout={options.tableLayout}
            label={options.showTitle !== false && options.title}
            value={node.value}
          />
        )}
      </FormField>
    );
  }
);

export const NumberWidgetPlugin: WidgetPlugin = {
  id: 'number-widget',
  use: (node) =>
    isNullableNumber(node.schema.type) || isNullableInteger(node.schema.type),
  Widget: NumberWidget,
};
